import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { getProducts } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { FaShoppingBag } from "react-icons/fa";

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await getProducts();
        setProducts(res.data || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="shop-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-syne font-bold text-white" data-testid="shop-title">Shop</h1>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <Skeleton className="aspect-square rounded-lg mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-5 w-1/4" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <FaShoppingBag className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-500">No products available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link 
                    to={`/shop/product/${product.id}`}
                    className="block group"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden mb-4 bg-zinc-900 border border-zinc-800 group-hover:border-zinc-600 transition-all">
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FaShoppingBag className="w-16 h-16 text-zinc-700" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-syne font-bold text-white group-hover:text-zinc-300 transition-colors mb-1">
                      {product.name}
                    </h3>
                    <p className="text-lg text-zinc-400">{formatPrice(product.price)}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
